import { gold, orange, white } from '@/theme/colors'
import styled from 'styled-components'

export const ColumnContainer = styled.div<{ $isDraggingOver?: boolean }>`
	background: ${white};
	border-radius: 12px;
	padding: 1rem;
	min-width: 350px;
	max-width: 410px;
	max-height: 100%;
	display: flex;
	flex-direction: column;
	transition: all 0.2s ease;
	border: 2px solid transparent;

	${props =>
		props.$isDraggingOver &&
		`
		border-color: ${orange};
		background: ${white};
		box-shadow: 0 4px 12px rgba(209, 95, 42, 0.15);
	`}

	@media (max-width: 600px) {
		min-width: calc(100vw - 2rem);
		max-width: calc(100vw - 2rem);
		padding: 0.875rem;
	}
`

export const ColumnHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	margin-bottom: 0.75rem;
	padding-bottom: 0.75rem;
	border-bottom: 2px solid #e9ecef;
	flex-shrink: 0;
`

export const ColumnTitle = styled.h2`
	margin: 0;
	font-size: 1.1rem;
	font-weight: 600;
	color: #2c3e50;
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
`

export const DateDisplay = styled.span`
	font-size: 0.9rem;
	font-weight: 400;
	color: #6c757d;
`

export const CustomDayName = styled.span`
	font-size: 0.95rem;
	font-weight: 600;
	color: ${gold};
	line-height: 1.3;
`

export const EditDayInfoButton = styled.button`
	flex-shrink: 0;
	padding: 0.25rem 0.5rem;
	background: transparent;
	border: 1.5px solid #e9ecef;
	border-radius: 6px;
	color: #9ca3af;
	font-size: 0.75rem;
	cursor: pointer;
	transition: all 0.2s ease;
	display: flex;
	align-items: center;
	gap: 0.25rem;
	margin-top: 0.125rem;
	white-space: nowrap;

	&:hover {
		border-color: ${gold};
		color: ${gold};
		background: #fffbf5;
	}
`

export const DayNoteRow = styled.button`
	width: 100%;
	text-align: left;
	background: #fffbf2;
	border: 1.5px solid #f5d99a;
	border-radius: 8px;
	padding: 0.625rem 0.75rem;
	margin-bottom: 0.75rem;
	cursor: pointer;
	transition: all 0.2s ease;
	display: flex;
	align-items: flex-start;
	gap: 0.5rem;
	flex-shrink: 0;

	&:hover {
		background: #fff4db;
		border-color: ${gold};
	}
`

export const StaticDayNoteRow = styled(DayNoteRow)`
	cursor: default;
`

export const DayNoteIcon = styled.span`
	font-size: 0.85rem;
	flex-shrink: 0;
	margin-top: 1px;
`

export const DayNoteText = styled.span`
	font-size: 0.82rem;
	color: #7a5c1e;
	line-height: 1.45;
	overflow: hidden;
	display: -webkit-box;
	-webkit-line-clamp: 3;
	-webkit-box-orient: vertical;
`

export const AddNoteRow = styled.button`
	width: 100%;
	text-align: left;
	background: transparent;
	border: 1.5px dashed #e9ecef;
	border-radius: 8px;
	padding: 0.5rem 0.75rem;
	margin-bottom: 0.75rem;
	cursor: pointer;
	transition: all 0.2s ease;
	display: flex;
	align-items: center;
	gap: 0.5rem;
	color: #adb5bd;
	font-size: 0.82rem;
	flex-shrink: 0;

	&:hover {
		border-color: ${gold};
		color: ${gold};
		background: #fffbf5;
	}
`

export const EventCount = styled.span`
	background: #007bff;
	color: white;
	padding: 0.25rem 0.5rem;
	border-radius: 12px;
	font-size: 0.8rem;
	font-weight: 500;
	min-width: 20px;
	text-align: center;
`

export const ColumnContent = styled.div`
	flex: 1;
	overflow-y: auto;
	overflow-x: hidden;
	min-height: 0;

	/* Custom scrollbar styling */
	&::-webkit-scrollbar {
		width: 6px;
	}

	&::-webkit-scrollbar-track {
		background: #f8f9fa;
		border-radius: 3px;
	}

	&::-webkit-scrollbar-thumb {
		background: #d1d5db;
		border-radius: 3px;

		&:hover {
			background: #9ca3af;
		}
	}

	/* Firefox */
	scrollbar-width: thin;
	scrollbar-color: #d1d5db #f8f9fa;
`

export const ButtonsContainer = styled.div`
	display: flex;
	gap: 0.5rem;
	flex-shrink: 0;
	padding-top: 0.5rem;
`

export const AddEventButton = styled.button`
	flex: 1;
	padding: 0.625rem;
	background: transparent;
	border: 2px dashed #ced4da;
	border-radius: 8px;
	color: #6c757d;
	font-size: 0.85rem;
	cursor: pointer;
	transition: all 0.2s ease;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 0.375rem;
	font-weight: 500;

	&:hover {
		border-color: ${orange};
		color: ${orange};
		background: #fff8f6;
	}

	&:active {
		transform: scale(0.98);
	}
`

export const AddEventForm = styled.form`
	background: white;
	border: 1px solid #e1e5e9;
	border-radius: 8px;
	padding: 1rem;
	margin-bottom: 0.75rem;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`

export const FormTitle = styled.h4`
	margin: 0 0 0.75rem 0;
	font-size: 0.9rem;
	font-weight: 600;
	color: #495057;
`

export const FormInput = styled.input`
	width: 100%;
	padding: 0.5rem;
	border: 1px solid #e1e5e9;
	border-radius: 4px;
	font-size: 0.9rem;
	margin-bottom: 0.5rem;
	transition: border-color 0.2s ease;

	&:focus {
		outline: none;
		border-color: #007bff;
	}
`

export const FormTextarea = styled.textarea`
	width: 100%;
	padding: 0.5rem;
	border: 1px solid #e1e5e9;
	border-radius: 4px;
	font-size: 0.9rem;
	resize: vertical;
	min-height: 60px;
	font-family: inherit;
	margin-bottom: 0.75rem;
	transition: border-color 0.2s ease;

	&:focus {
		outline: none;
		border-color: #007bff;
	}
`

export const FormActions = styled.div`
	display: flex;
	gap: 0.5rem;
	justify-content: flex-end;
`

export const FormButton = styled.button`
	padding: 0.5rem 1rem;
	border: none;
	border-radius: 4px;
	font-size: 0.9rem;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease;

	&.primary {
		background: #007bff;
		color: white;

		&:hover {
			background: #0056b3;
		}
	}

	&.secondary {
		background: #6c757d;
		color: white;

		&:hover {
			background: #545b62;
		}
	}
`

export const EmptyState = styled.div`
	text-align: center;
	color: #6c757d;
	padding: 2rem 1rem;
	border: 2px dashed #e9ecef;
	border-radius: 8px;
	background: #f8f9fa;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 1rem;
`

export const EmptyStateText = styled.div`
	font-size: 0.9rem;
	color: #6c757d;
	margin: 0;
`

export const DraggedEventPlaceholder = styled.div`
	height: 0;
	overflow: hidden;
`

export const TimelineEventRow = styled.div`
	display: flex;
	align-items: stretch;
`

export const EventDotColumn = styled.div`
	width: 24px;
	flex-shrink: 0;
	display: flex;
	flex-direction: column;
	align-items: center;
`

export const EventDotLineAbove = styled.div<{ $first: boolean }>`
	height: 31px;
	width: 0;
	border-left: ${props => (props.$first ? 'none' : '2px dashed #d4a574')};
`

export const EventDot = styled.div`
	width: 10px;
	height: 10px;
	border-radius: 50%;
	background: white;
	border: 2px solid #d4a574;
	flex-shrink: 0;
	position: relative;
	z-index: 1;
`

export const EventDotLine = styled.div<{ $last?: boolean }>`
	flex: 1;
	width: 0;
	border-left: 2px dashed #d4a574;
	min-height: 8px;
	${props => props.$last && 'margin-bottom: 20px;'}
`

export const EventContent = styled.div`
	flex: 1;
	min-width: 0;
	padding-left: 10px;
	padding-top: 10px;
	padding-bottom: 10px;
`

export const DropPlaceholder = styled.div`
	background: #e3f2fd;
	border: 2px dashed #2196f3;
	border-radius: 8px;
	margin-bottom: 0.75rem;
	height: 60px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #1976d2;
	font-size: 0.9rem;
	font-weight: 500;
	transition: all 0.2s ease;
	animation: pulse 1.5s ease-in-out infinite;

	@keyframes pulse {
		0% {
			opacity: 0.6;
		}
		50% {
			opacity: 1;
		}
		100% {
			opacity: 0.6;
		}
	}
`
